import { EnvVar, envOrError } from "@utils/envHelpers";
import { helperFetch } from "@utils/fetchHelpers";

import { getAzureAdToken } from "./authorizationClient";

export type Redirect = {
  id: string;
  oldUrl: string;
  newUrl: string;
  statusCode: number;
  wildCard: boolean;
  lastUpdated: string;
};

export type Order = {
  organisation?: string | undefined;
  name: string;
  email: string;
  address: string;
  zipCode: string;
  state: string;
  orders: Array<{
    fNumber: string;
    name: string;
    amount: number;
  }>;
  recipientAddress: string;
};

type RedirectToAdd = Omit<Redirect, "id" | "lastUpdated">;

export type PressReleaseImage = {
  url?: string | null;
  title?: string | null;
  focus?: string | null;
  caption?: string | null;
  license?: string | null;
  copyright?: string | null;
  normal?: string | null;
  thumbnail_original?: string | null;
  thumbnail_square?: string | null;
  thumbnail_4_3?: string | null;
  thumbnail_16_9?: string | null;
};

export type PressRelease = {
  id: string;
  source: string;
  slug?: string | null;
  heading?: string | null;
  preamble?: string | null;
  body?: string | null;
  categories?: string[] | null;
  publishDate?: string | null;
  updatedDate?: string | null;
  images?: PressReleaseImage[] | null;
  mainImage?: PressReleaseImage | null;
  summary?: string | null;
  image_caption?: string | null;
};

export type PressReleaseFlat = {
  id: string;
  heading?: string | null;
  slug?: string | null;
  publishDate?: string | null;
};

export type QuizCompetitionEntry = {
  name: string;
  email: string;
  address: string;
  zipCode: string;
  state: string;
  recipientAddress: string;
};

function getBaseUrl(): string {
  const apiGatewayUrl = envOrError(EnvVar.API_GATEWAY_URL);
  return `${apiGatewayUrl}/afase-api/v1`;
}

async function headers() {
  const scope = envOrError(EnvVar.AFASE_API_APP_REGISTRATION_CLIENT_ID);
  const accessToken = await getAzureAdToken(scope);
  return {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${accessToken}`, // Validated by gateway
    "X-ApiKey": envOrError(EnvVar.AFASE_API_KEY), // Validated by afase-api
  };
}

export async function getRedirects(): Promise<Response> {
  return await fetch(`${getBaseUrl()}/redirects`, {
    method: "GET",
    cache: "no-cache",
    headers: await headers(),
  });
}

export async function addRedirect(redirect: RedirectToAdd): Promise<Response> {
  return await fetch(`${getBaseUrl()}/redirects`, {
    method: "POST",
    cache: "no-cache",
    body: JSON.stringify(redirect),
    headers: await headers(),
  });
}

export async function updateRedirect(redirect: Redirect): Promise<Response> {
  return await fetch(`${getBaseUrl()}/redirects`, {
    method: "PUT",
    cache: "no-cache",
    body: JSON.stringify(redirect),
    headers: await headers(),
  });
}

export async function deleteRedirect(redirect: { id: string }): Promise<Response> {
  return await fetch(`${getBaseUrl()}/redirects`, {
    method: "DELETE",
    cache: "no-cache",
    body: JSON.stringify(redirect),
    headers: await headers(),
  });
}

export async function postOrder(order: Order): Promise<Response> {
  return await fetch(`${getBaseUrl()}/orders`, {
    method: "POST",
    cache: "no-cache",
    body: JSON.stringify(order),
    headers: await headers(),
  });
}

export const getPressReleases = async (): Promise<PressRelease[]> => {
  const response = await helperFetch(`${getBaseUrl()}/pressreleases`, {
    method: "GET",
    headers: await headers(),
  });
  return await response.json();
};

export const getPressReleasesFlat = async (): Promise<PressReleaseFlat[]> => {
  const response = await helperFetch(`${getBaseUrl()}/pressreleases?flat=true`, {
    method: "GET",
    headers: await headers(),
  });
  return await response.json();
};

export const getPressReleaseBySlug = async (slug: string): Promise<PressRelease | null> => {
  const response = await helperFetch(`${getBaseUrl()}/pressreleases/${slug}`, {
    method: "GET",
    headers: await headers(),
    throwErrorOnNotFound: false,
  });
  if (response.status === 404) {
    return null;
  }
  if (response.status === 200) {
    return await response.json();
  }
  throw new Error(`Failed to fetch press release with slug ${slug}`);
};

export async function postQuizCompetitionEntry(entry: QuizCompetitionEntry): Promise<Response> {
  return await fetch(`${getBaseUrl()}/quiz/compete`, {
    method: "POST",
    cache: "no-cache",
    body: JSON.stringify(entry),
    headers: await headers(),
  });
}

export async function getRedirect(url: string): Promise<Redirect | null> {
  if (url.length === 0) {
    return Promise.resolve(null);
  }

  const response = await helperFetch(`${getBaseUrl()}/redirects?url=${encodeURIComponent(url)}`, {
    method: "GET",
    headers: await headers(),
    timeout: 5_000,
  });

  const data = await response.json();
  const redirect = data.redirects[0];

  if (!redirect) {
    return null;
  }

  return redirect;
}
export async function addKrWebForm(krwebform: any): Promise<Response> {
  return await fetch(`${getBaseUrl()}/krwebform`, {
    method: "POST",
    cache: "no-cache",
    body: JSON.stringify(krwebform),
    headers: await headers(),
  });
}
